import React, { useState } from 'react';
import AuthGlass, { authGlassSx } from './AuthGlass';
import { Modal, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationAlert from '../shared/NotificationAlert';
import AuthTextField from './AuthTextField';
import PrimaryButton from './PrimaryButton';
import AsyncButtonContent, { asyncButtonState, useSuccessFlash } from '../motion/AsyncButtonContent';
import authService from '../../services/authService';
import type { ClientType } from './AuthPanel';
interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
  clientType?: ClientType;
}
const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  open,
  onClose,
  clientType = 'C'
}) => {
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCodeFields, setShowCodeFields] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [codeSentFlash, flashCodeSent] = useSuccessFlash();
  const [codeResentFlash, flashCodeResent] = useSuccessFlash();
  const [resetFlash, flashReset] = useSuccessFlash();
  const hasInlineNotification = Boolean(error || successMessage);

  // Validation functions
  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);
  const validatePasswordStrength = (password: string) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasMinLength = password.length >= 8;
    return hasUppercase && hasLowercase && hasNumber && hasMinLength;
  };
  const doPasswordsMatch = (password: string, confirmPassword: string) => {
    return password === confirmPassword;
  };
  const handleSendCode = async () => {
    if (sendingCode) return;
    setError('');
    setSuccessMessage('');
    setEmailCode('');

    // Frontend validation
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setSendingCode(true);
    try {
      const response = await authService.sendEmailCode({
        clientType: clientType,
        email: email
      });
      if (response.success) {
        // First send: hold the check on the send button before the code fields replace it.
        if (showCodeFields) flashCodeResent();
        else await flashCodeSent(600);
        setShowCodeFields(true);
        setSuccessMessage('Verification code sent to your email!');

        // Auto-dismiss success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        setError(response.message || 'Failed to send verification code');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  };
  const handleResetPassword = async () => {
    if (resettingPassword || resetFlash) return;
    setError('');
    setSuccessMessage('');

    // Frontend validation
    if (!emailCode) {
      setError('Please enter the verification code');
      return;
    }
    if (emailCode.length !== 6) {
      setError('Verification code must be 6 digits');
      return;
    }
    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }
    if (!confirmPassword) {
      setError('Please confirm your new password');
      return;
    }
    if (!doPasswordsMatch(newPassword, confirmPassword)) {
      setError('Your passwords don\'t match. Please check and try again.');
      return;
    }
    if (!validatePasswordStrength(newPassword)) {
      setError('Password must be at least 8 characters and include uppercase, lowercase, and a number.');
      return;
    }
    setResettingPassword(true);
    try {
      await authService.resetPassword({
        clientType: clientType,
        email: email,
        emailCode: emailCode,
        password: newPassword,
        confirmPassword: confirmPassword
      });

      // Success!
      setSuccessMessage('Password reset successful! You can now log in with your new password.');
      await flashReset(600);

      // Close modal after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
      // Keep fields editable so user can fix issues
    } finally {
      setResettingPassword(false);
    }
  };
  const handleResendCode = async () => {
    setEmailCode(''); // Clear the code field
    await handleSendCode(); // Reuse the send code logic
  };
  const handleClose = () => {
    // Reset all state
    setEmail('');
    setEmailCode('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCodeFields(false);
    setError('');
    setSuccessMessage('');
    setSendingCode(false);
    setResettingPassword(false);
    onClose();
  };
  return <Modal open={open} onClose={handleClose} sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }} BackdropProps={{
    sx: {
      backdropFilter: 'blur(8px)',
      backgroundColor: 'rgba(0, 0, 0, 0.5)'
    }
  }}>
      <Box sx={{
      position: 'relative',
      width: '100%',
      maxWidth: '600px',
      height: showCodeFields ? 'auto' : {
        xs: 'auto',
        md: '350px'
      },
      ...authGlassSx,
      borderRadius: {
        xs: '16px',
        md: '25px'
      },
      border: 'none',
      padding: {
        xs: hasInlineNotification ? '119.5px 21.5px 33.5px 21.5px' : '71.5px 21.5px 33.5px 21.5px',
        md: '96.5px 65.5px 49.5px 65.5px'
      },
      outline: 'none',
      mx: {
        xs: 2,
        md: 0
      },
      boxSizing: 'border-box'
    }}>
        <AuthGlass />
        {/* Notification Alert - Floating Top Center */}
        {(error || successMessage) && <Box sx={{
        position: 'absolute',
        top: {
          xs: '16px',
          md: '20px'
        },
        left: {
          xs: 0,
          md: '50%'
        },
        right: {
          xs: 0,
          md: 'auto'
        },
        transform: {
          xs: 'none',
          md: 'translateX(-50%)'
        },
        zIndex: 10,
        width: {
          xs: '100%',
          md: 'fit-content'
        },
        px: {
          xs: '20px',
          md: 0
        },
        display: 'flex',
        justifyContent: 'center',
        boxSizing: 'border-box'
      }}>
            {error ? <NotificationAlert type="error" message={error} onClose={() => setError('')} sx={{
          whiteSpace: 'nowrap'
        }} mode="inline" /> : <NotificationAlert type="success" message={successMessage} onClose={() => setSuccessMessage('')} sx={{
          whiteSpace: 'nowrap'
        }} mode="inline" />}
          </Box>}

        {/* Close button */}
        <IconButton onClick={handleClose} sx={{
        position: 'absolute',
        top: 16,
        right: 16,
        color: 'white',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
      }}>
          <CloseIcon />
        </IconButton>

        {/* Content */}
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3
      }}>
          {/* Title */}
          <Box>
            <Typography sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 'bold',
            fontSize: "var(--type-display)",
            color: 'white',
            lineHeight: 'normal'
          }}>
              Reset Your Password
            </Typography>
            <Typography sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: "var(--type-body)",
            color: 'white',
            opacity: 0.8,
            mt: 1
          }}>
              {!showCodeFields ? 'Enter your email to receive a verification code' : 'Enter the code and your new password'}
            </Typography>
          </Box>

          {/* Email field */}
          <Box sx={{
          width: '100%',
          maxWidth: '455px'
        }}>
            <AuthTextField placeholder="Email address" type="email" value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} disabled={showCodeFields} />
          </Box>

          {/* Conditional: Code and password fields (shown after code is sent) */}
          {showCodeFields && <>
              {/* Verification code */}
              <Box sx={{
              width: '100%',
              maxWidth: '455px'
            }}>
                <AuthTextField placeholder="Enter 6-digit code" type="text" value={emailCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              // Only allow numbers and max 6 digits
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setEmailCode(value);
            }} inputProps={{
              maxLength: 6
            }} />
              </Box>

              {/* New password */}
              <Box sx={{
              width: '100%',
              maxWidth: '455px'
            }}>
                <AuthTextField placeholder="New password" type="password" value={newPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)} />
              </Box>

              {/* Confirm password */}
              <Box sx={{
              width: '100%',
              maxWidth: '455px'
            }}>
                <AuthTextField placeholder="Confirm new password" type="password" value={confirmPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)} />
              </Box>

              {/* Password hint */}
              <Typography sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: "var(--type-caption)",
            color: 'rgba(255, 255, 255, 0.7)',
            mt: -2,
            px: 1
          }}>
                Must be at least 8 characters with uppercase, lowercase, and a number
              </Typography>
            </>}

          {/* Button area */}
          {!showCodeFields ?
        // Before code sent: Show "Send Code" button
        <Box sx={{
          width: '100%',
          maxWidth: '455px'
        }}>
              <PrimaryButton onClick={handleSendCode} aria-busy={sendingCode || codeSentFlash} sx={{
            width: '100%',
            height: '40px'
          }}>
                <AsyncButtonContent state={asyncButtonState(sendingCode, codeSentFlash)} iconSize={24} loadingLabel="Sending code">Send Verification Code</AsyncButtonContent>
              </PrimaryButton>
            </Box> :
        // After code sent: Show "Resend" and "Reset Password" buttons
        <Box sx={{
          display: 'flex',
          gap: 1,
          width: '100%',
          maxWidth: '455px'
        }}>
              <PrimaryButton onClick={handleResendCode} aria-busy={sendingCode || codeResentFlash} sx={{
            width: '35%',
            height: '40px',
            backgroundColor: '#9d8985',
            color: 'white',
            '&:hover': {
              backgroundColor: '#8b7a77'
            },
            '&:disabled': {
              backgroundColor: '#b5a9a6',
              color: 'rgba(255, 255, 255, 0.7)'
            }
          }}>
                <AsyncButtonContent state={asyncButtonState(sendingCode, codeResentFlash)} iconSize={20} loadingLabel="Sending code">Resend Code</AsyncButtonContent>
              </PrimaryButton>
              <PrimaryButton onClick={handleResetPassword} aria-busy={resettingPassword || resetFlash} sx={{
            width: '65%',
            height: '40px'
          }}>
                <AsyncButtonContent state={asyncButtonState(resettingPassword, resetFlash)} iconSize={24} loadingLabel="Resetting password">Reset Password</AsyncButtonContent>
              </PrimaryButton>
            </Box>}
        </Box>
      </Box>
    </Modal>;
};
export default ForgotPasswordModal;
